import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { ConditionalWrapper } from '@supplement-bacon/react-toolkit/utils'
import { Avatar, Flex, Modal, Space, Tooltip, Typography } from 'antd'
import { AvatarProps } from 'antd/es/avatar'
import { FC, useState } from 'react'
import { Link } from 'react-router-dom'
import { User } from '../../models/User'
import { useAppSelector } from '../../store/hooks'
import { selectUserById } from '../../store/UserReducer'
import { Permission } from '../../utils/permissions'
import { useHasAccess } from '../HasAccess'

const { Text } = Typography

type Props = SBModelComponent<User> & {
  photoUrl?: string
  size?: AvatarProps['size']
  type?: 'full' | 'condensed'
  noLinkToDetails?: boolean
  /**
   * If true, clicking on the picture will open a modal with the full-size image
   * @default false
   */
  pictureOpensInModal?: boolean
  /** When set, truncates the displayed name with ellipsis at this pixel width. */
  nameMaxWidth?: number
}

export const UserAvatar: FC<Props> = ({
  id,
  model,
  size = 'default',
  type = 'full',
  photoUrl,
  noLinkToDetails = false,
  pictureOpensInModal = false,
  nameMaxWidth,
}) => {
  const user = model ? model : useAppSelector(selectUserById(id!))
  const { hasAccess } = useHasAccess()

  const [fullAvatarModalIsOpen, setFullAvatarModalIsOpen] =
    useState<boolean>(false)
  if (!user) {
    return <></>
  }

  return (
    <ConditionalWrapper
      condition={noLinkToDetails === false && hasAccess(Permission.readUser)}
      wrapper={(children) => (
        <Link to={`/users/${user.id}/informations`}>{children}</Link>
      )}
    >
      <Space orientation="horizontal">
        <Tooltip
          title={`${user.firstName} ${user.lastName ?? ''}`}
          open={type == 'full' || noLinkToDetails ? false : undefined}
        >
          <Avatar
            onClick={() => {
              if (!photoUrl || !pictureOpensInModal) {
                return
              }
              setFullAvatarModalIsOpen(true)
            }}
            src={photoUrl}
            size={size == 'large' ? 60 : size}
            style={{
              color: '#fff',
              backgroundColor: '#8e978a',
              fontSize: size == 'small' ? 11 : undefined,
              border: 0,
              cursor: 'pointer',
            }}
          >
            {!photoUrl && (
              <div style={{ textTransform: 'uppercase' }}>
                {user.firstName && user.firstName.slice(0, 1)}
                {user.lastName && user.lastName.slice(0, 1)}
                {!user.firstName && !user.lastName && '--'}
              </div>
            )}
          </Avatar>
          {photoUrl && pictureOpensInModal && (
            <>
              <Modal
                open={fullAvatarModalIsOpen}
                onCancel={() => setFullAvatarModalIsOpen(false)}
                footer={<></>}
              >
                <Flex align="center" justify="center">
                  <Avatar size={350} src={photoUrl}></Avatar>
                </Flex>
              </Modal>
            </>
          )}
        </Tooltip>
        {type == 'full' && (
          <Space.Compact orientation="vertical">
            <Space orientation="horizontal">
              <Text
                style={nameMaxWidth ? { maxWidth: nameMaxWidth } : undefined}
                ellipsis={
                  nameMaxWidth
                    ? { tooltip: `${user.firstName} ${user.lastName}` }
                    : false
                }
              >
                {user.firstName} {user.lastName}
              </Text>
            </Space>
          </Space.Compact>
        )}
      </Space>
    </ConditionalWrapper>
  )
}

export type UserAvatarType = { Avatar: typeof UserAvatar }
